import { useState, useEffect } from 'react';
import { useDebounce } from './useDebounce';
import { knowledgeBaseShareService } from '../services/knowledgeBaseShareService';
import { UserSearchUser } from '../schemas/knowledgeBase';

interface UseUserSearchReturn {
  query: string;
  setQuery: (query: string) => void;
  results: UserSearchUser[];
  loading: boolean;
  error: string | null;
}

/**
 * Custom hook for searching users with debouncing
 * @param currentUserId - The current user's ID (will be filtered from results)
 * @param debounceDelay - Debounce delay in milliseconds (default: 500)
 */
export function useUserSearch(
  currentUserId: number,
  debounceDelay: number = 500
): UseUserSearchReturn {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<UserSearchUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const debouncedQuery = useDebounce(query, debounceDelay);

  useEffect(() => {
    const searchUsers = async () => {
      // Don't search if query is empty
      if (!debouncedQuery.trim()) {
        setResults([]);
        setLoading(false);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await knowledgeBaseShareService.searchUsers(debouncedQuery, 10);
        // Filter out current user from results
        const filteredResults = response.items.filter(user => user.id !== currentUserId);
        setResults(filteredResults);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to search users';
        setError(errorMessage);
        setResults([]);
      } finally {
        setLoading(false);
      }
    };

    searchUsers();
  }, [debouncedQuery, currentUserId]);

  return { query, setQuery, results, loading, error };
}
